const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const IS_PRODUCTION = import.meta.env.MODE === 'production';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let hasInitialized = false;
const pendingPageViews: string[] = [];
type QueuedEvent = { name: string; params?: Record<string, unknown> };
const pendingEvents: QueuedEvent[] = [];

const canTrack = () =>
  typeof window !== 'undefined' && Boolean(GA_MEASUREMENT_ID) && IS_PRODUCTION;

const sendPageView = (path: string) => {
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

const sendEvent = (name: string, params?: Record<string, unknown>) => {
  window.gtag?.('event', name, params);
};

const flushPendingPageViews = () => {
  if (!hasInitialized || typeof window.gtag !== 'function') {
    return;
  }
  while (pendingPageViews.length > 0) {
    const path = pendingPageViews.shift();
    if (path) {
      sendPageView(path);
    }
  }
};

const flushPendingEvents = () => {
  if (!hasInitialized || typeof window.gtag !== 'function') {
    return;
  }
  while (pendingEvents.length > 0) {
    const event = pendingEvents.shift();
    if (event) {
      sendEvent(event.name, event.params);
    }
  }
};

export const initAnalytics = (): void => {
  if (!canTrack() || hasInitialized) {
    return;
  }

  if (typeof window.gtag === 'function') {
    hasInitialized = true;
    flushPendingPageViews();
    flushPendingEvents();
  } else {
    const script = document.getElementById('ga4-script');
    if (script) {
      script.addEventListener(
        'load',
        () => {
          hasInitialized = true;
          flushPendingPageViews();
          flushPendingEvents();
        },
        { once: true },
      );
    }
  }
};

export const trackPageView = (path: string): void => {
  if (!canTrack() || !hasInitialized || typeof window.gtag !== 'function') {
    pendingPageViews.push(path);
    return;
  }
  sendPageView(path);
};

export const trackEvent = (name: string, params?: Record<string, unknown>): void => {
  if (!canTrack() || !hasInitialized || typeof window.gtag !== 'function') {
    pendingEvents.push({ name, params });
    return;
  }
  sendEvent(name, params);
};
